import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';

import logoColor from '@/assets/brand/signcare-logo.svg';
import mobileFrame from '@/assets/images/mobile-frame.png';
import { cn } from '@/lib/utils';
import {
  AadhaarDigilockerScreen,
  AadhaarOcrScreen,
  AadhaarSecureQrScreen,
  BankAccountScreen,
  DrivingLicenceScreen,
  ElectricityScreen,
  FaceMatchPrepareScreen,
  FaceMatchScreen,
  GstVerificationScreen,
  LpgScreen,
  MsmeScreen,
  PanVerificationLite,
  PanVerificationOCR,
  PanVerificationStandard,
  PassportScreen,
  PngScreen,
  RationCardScreen,
  SelfieCaptureScreen,
  SuccessScreen,
  VoterIdScreen,
  WelcomeConsentNotice,
  type WelcomePreviewStep,
  WelcomeScreen,
} from './preview-screens';

// Phone frame geometry, taken from the designer's frame image so the preview is
// the same size as theirs (react-new-html `.sc-workflow-preview__phone`).
const PHONE_W = 388;
const PHONE_H = 802;

// Phone-preview mock. The screen list is the applicant's JOURNEY: the welcome
// overview, then one screen per workflow step showing that step's first-priority
// enabled method, then the green success screen. So a 4-step workflow previews as
// welcome + 4 + success, and paging through it walks the real flow.
//
// The host resolves the journey (it owns the catalogue and the step list) and
// controls the screen index, so selecting a step or toggling its method focuses
// the phone on that step. To add an API, add an entry to SCREEN_BY_OPERATION and
// a count to INPUT_COUNT_BY_OPERATION.

type PreviewScreen = {
  node: ReactNode;
  cta: string;
  key: string;
  /** Extra copy rendered in the fixed footer, under the CTA (welcome consent). */
  footerNote?: ReactNode;
  /**
   * The step's name, captioned UNDER the phone (as the designer's preview does).
   * Outside the phone on purpose: it tells the builder which screen they are
   * looking at without occupying room the applicant's screen needs.
   */
  label?: string;
};
type ScreenSpec = { node: ReactNode; cta: string };
/**
 * A factory may return SEVERAL screens for one workflow step. Face Match is the first
 * to need it — the applicant reads the instructions, then the camera opens — and it
 * is a property of the method, not of the workflow, so the step count is unchanged
 * while the journey gains a screen.
 */
type ScreenFactory = (
  dob: string,
  setDob: (v: string) => void,
  dobRequired: boolean,
) => ScreenSpec | ScreenSpec[];

// Per-API preview screen, keyed by catalogue operationId.
//
// DigiLocker used to be excluded here on the grounds that it "redirects out of the
// app, so there is no screen". That was wrong: the applicant is not redirected
// immediately — they first see an in-app screen asking for the mobile number linked
// to their Aadhaar, and only Continue sends them to DigiLocker. That screen is real,
// AadhaarDigilockerScreen already existed for it, and leaving it unmapped meant
// selecting either DigiLocker method added a step to the workflow that the builder's
// preview silently skipped.
//
// (OVSE stays absent deliberately — it is seeded isActive: false, so no workflow can
// select it and a preview screen would be unreachable.)
const SCREEN_BY_OPERATION: Record<string, ScreenFactory> = {
  // PAN — priority order per spec: Verification → Lite → OCR.
  Post_Pan_Verify: (dob, setDob, dobRequired) => ({
    node: <PanVerificationStandard dob={dob} setDob={setDob} dobRequired={dobRequired} />,
    cta: 'Continue',
  }),
  Post_Pan_Lite: (dob, setDob, dobRequired) => ({
    node: <PanVerificationLite dob={dob} setDob={setDob} dobRequired={dobRequired} />,
    cta: 'Continue',
  }),
  Post_Pan_Ocr: () => ({ node: <PanVerificationOCR />, cta: 'Capture' }),
  // Aadhaar — all four selectable methods preview. The two DigiLocker variants share
  // one screen (mobile number linked to Aadhaar); `withPin` only changes the
  // explanatory line, because the field the applicant fills in is identical.
  Post_Aadhaar_Digilocker_Pinless: () => ({
    node: <AadhaarDigilockerScreen />,
    cta: 'Continue',
  }),
  Post_Aadhaar_Digilocker_Pin: () => ({
    node: <AadhaarDigilockerScreen withPin />,
    cta: 'Continue',
  }),
  Post_Aadhaar_SecureQr: () => ({ node: <AadhaarSecureQrScreen />, cta: 'Capture' }),
  Post_Aadhaar_Ocr: () => ({ node: <AadhaarOcrScreen />, cta: 'Capture' }),
  // Face Match — AWS only. Two screens: prepare, then capture.
  Post_Face_Match_Aws: () => [
    { node: <FaceMatchPrepareScreen />, cta: 'Continue' },
    { node: <FaceMatchScreen />, cta: 'Capture' },
  ],
  // Liveness — SignCare Passive only. Same two-screen shape as Face Match, and the
  // SAME capture screen: both put the applicant's face in the circular frame, so
  // giving them one component keeps them from drifting apart.
  Post_Liveness_Passive: () => [
    // The SAME prepare screen as Face Match, retitled. The old bespoke Liveness
    // screen (scanner frame + eye icon) is retired: the two steps ask the applicant
    // for the identical thing, so showing them two different designs made one product
    // look like two.
    {
      node: (
        <FaceMatchPrepareScreen
          title="Liveness Check"
          subtitle="Confirm you’re a real, live person"
        />
      ),
      cta: 'Continue',
    },
    { node: <SelfieCaptureScreen title="Liveness Check" />, cta: 'Capture' },
  ],
  // Bank Account Verification — 4 methods, same input (account + IFSC) but each
  // preview is labelled with its method and shows the penny-drop note when relevant.
  Post_Bank_PennyDrop_Basic: () => ({
    node: <BankAccountScreen method="Penny Drop - Basic" pennyDrop />,
    cta: 'Continue',
  }),
  Post_Bank_PennyDrop_Advance: () => ({
    node: <BankAccountScreen method="Penny Drop - Advance" pennyDrop />,
    cta: 'Continue',
  }),
  Post_Bank_Penniless_Basic: () => ({
    node: <BankAccountScreen method="Penniless - Basic" />,
    cta: 'Continue',
  }),
  Post_Bank_Penniless_Advance: () => ({
    node: <BankAccountScreen method="Penniless - Advance" />,
    cta: 'Continue',
  }),
  // GST Verification — single method (Whitebooks basic GSTIN lookup).
  Post_Gst_Verify: () => ({
    node: <GstVerificationScreen method="GST Verification" />,
    cta: 'Continue',
  }),
  // ID Verification — 3 document methods (the applicant picks one at run time).
  Post_VoterID_Verify: () => ({ node: <VoterIdScreen />, cta: 'Continue' }),
  Post_Passport_Verify: () => ({ node: <PassportScreen />, cta: 'Continue' }),
  Post_DL_Verify: () => ({ node: <DrivingLicenceScreen />, cta: 'Continue' }),
  // Utility Bill — 3 connection-type methods (Electricity / PNG / LPG).
  Post_Electricity_Verify: () => ({ node: <ElectricityScreen />, cta: 'Continue' }),
  Post_Png_Verify: () => ({ node: <PngScreen />, cta: 'Continue' }),
  Post_Lpg_Verify: () => ({ node: <LpgScreen />, cta: 'Continue' }),
  // Ration Card Verification — single method (Protean).
  Post_RationCard_Details: () => ({
    node: <RationCardScreen method="Ration Card Verification" />,
    cta: 'Continue',
  }),
  // MSME Verification (Udyam) — single method (Udyam without OTP, Protean).
  Post_Msme_Udyam: () => ({
    node: <MsmeScreen method="MSME Verification (Udyam)" />,
    cta: 'Continue',
  }),
};

// True when an API has an in-app preview screen (drives the Method-tab focus jump
// and the dynamic screen list).
export function hasPreviewScreen(operationId: string): boolean {
  return operationId in SCREEN_BY_OPERATION;
}

// Fallback ordered operationIds used only until the caller has resolved the
// enabled set (catalogue still loading). Mirrors the full default per category.
function categoryOperationIds(stepType: string | undefined): string[] {
  if (stepType && /aadhaar/i.test(stepType)) {
    // All four selectable methods, in the catalogue's own priority order.
    return [
      'Post_Aadhaar_Digilocker_Pinless',
      'Post_Aadhaar_Digilocker_Pin',
      'Post_Aadhaar_SecureQr',
      'Post_Aadhaar_Ocr',
    ];
  }
  if (stepType && /liveness/i.test(stepType)) {
    return ['Post_Liveness_Passive'];
  }
  if (stepType && /face/i.test(stepType)) {
    return ['Post_Face_Match_Aws'];
  }
  if (stepType && /bank/i.test(stepType)) {
    return [
      'Post_Bank_PennyDrop_Basic',
      'Post_Bank_PennyDrop_Advance',
      'Post_Bank_Penniless_Basic',
      'Post_Bank_Penniless_Advance',
    ];
  }
  if (stepType && /gst/i.test(stepType)) {
    return ['Post_Gst_Verify'];
  }
  if (stepType && /id_verif/i.test(stepType)) {
    return ['Post_VoterID_Verify', 'Post_Passport_Verify', 'Post_DL_Verify'];
  }
  if (stepType && /utility_bill/i.test(stepType)) {
    return ['Post_Electricity_Verify', 'Post_Png_Verify', 'Post_Lpg_Verify'];
  }
  if (stepType && /ration/i.test(stepType)) {
    return ['Post_RationCard_Details'];
  }
  if (stepType && /msme/i.test(stepType)) {
    return ['Post_Msme_Udyam'];
  }
  return ['Post_Pan_Verify', 'Post_Pan_Lite', 'Post_Pan_Ocr'];
}

// Journey used only until the caller resolves the real one (catalogue still
// loading): the selected step's category screens, so the phone isn't empty.
function fallbackJourney(stepType: string | undefined): JourneyScreen[] {
  return categoryOperationIds(stepType).map((operationId) => ({
    key: operationId,
    operationId,
  }));
}

/** Placeholder setter for a measuring call, which discards the screens it builds. */
const NO_OP = () => {};

/**
 * The screens ONE journey entry expands to — usually one, but two for a step whose method
 * shows instructions before opening the camera.
 *
 * THE ONLY PLACE THAT EXPANSION HAPPENS. `previewScreenCount` measures an entry by calling this,
 * so a caller mapping a step to a screen index can never disagree with what is actually rendered.
 * Keeping the count as a separate list of "these operations have two screens" is exactly how the
 * off-by-one below was introduced.
 */
function screensFor(
  entry: JourneyScreen,
  dob: string,
  setDob: (v: string) => void,
): PreviewScreen[] {
  // A step that supplied its own screens (no provider, so nothing to look up).
  const specs = entry.screens?.length
    ? entry.screens.map((s) => ({ node: s.node, cta: s.cta ?? 'Continue' }))
    : resolveFromCatalogue(entry, dob, setDob);

  return specs.map((spec, i) => ({
    ...spec,
    // Stable key = the workflow step id, so the pagination dots keep identity as steps are
    // reordered or their method changes. A step contributing more than one screen suffixes the
    // index, since two dots cannot share a key.
    key: specs.length > 1 ? `${entry.key}:${i}` : entry.key,
    label: entry.label,
  }));
}

/** The screens for a step whose method comes from the API catalogue. Empty when it has none. */
function resolveFromCatalogue(
  entry: JourneyScreen,
  dob: string,
  setDob: (v: string) => void,
): ScreenSpec[] {
  const factory = SCREEN_BY_OPERATION[entry.operationId];
  if (!factory) return [];
  const produced = factory(dob, setDob, entry.dobRequired ?? false);
  return Array.isArray(produced) ? produced : [produced];
}

/**
 * How many preview screens a journey entry occupies: 2 for Face Match / Liveness (prepare then
 * capture), 0 for a step whose method has no screen at all, 1 otherwise.
 *
 * Measured by building the screens rather than declared, so it cannot drift from them. That is
 * cheap — the factories only allocate React elements, they do not render.
 */
export function previewScreenCount(entry: JourneyScreen): number {
  return screensFor(entry, '', NO_OP).length;
}

/**
 * The index of a step's FIRST preview screen, or -1 if it has none.
 *
 * WHY THIS EXISTS. The journey has one entry per workflow STEP, but the screen list is longer,
 * because Face Match and Liveness each contribute two. Callers used to compute
 * `journey.findIndex(...) + 1`, which is a step number being used as a screen number: correct up to
 * the first two-screen step and then off by one more for each one passed. Selecting Liveness landed
 * on Face Match's camera, and selecting Bank Account landed on Liveness's instructions — the phone
 * showed a screen belonging to a different step than the one being edited.
 *
 * The `+ 1` for the welcome overview at screen 0 is included here, so no caller has to remember it.
 */
export function firstScreenIndexForStep(
  journey: JourneyScreen[] | undefined,
  stepKey: string | undefined,
): number {
  if (!journey || !stepKey) return -1;

  let index = 1; // Screen 0 is always the welcome overview.
  for (const entry of journey) {
    const count = previewScreenCount(entry);
    // A step with no screen of its own gets -1 rather than `index`, which at this point is the
    // NEXT step's first screen — focusing the phone there would be the same mismatch again.
    if (entry.key === stepKey) return count > 0 ? index : -1;
    index += count;
  }
  return -1;
}

function buildScreens(
  journey: JourneyScreen[],
  dob: string,
  setDob: (v: string) => void,
  welcome: WelcomeConfig,
): PreviewScreen[] {
  // Screen 0 is always the welcome overview — it describes the whole workflow, not
  // the selected step, so it stays put as the selection changes.
  const welcomeScreen: PreviewScreen = {
    node: (
      <WelcomeScreen
        steps={welcome.steps}
        heading={welcome.heading}
        subheading={welcome.subheading}
        logoUrl={welcome.logoUrl}
      />
    ),
    cta: 'Continue',
    key: 'welcome',
    footerNote: <WelcomeConsentNotice />,
  };
  const success: PreviewScreen = { node: <SuccessScreen />, cta: 'Done', key: 'success' };
  const screens = journey.flatMap((entry) => screensFor(entry, dob, setDob));
  // Welcome overview first, green success screen last.
  return [welcomeScreen, ...screens, success];
}

/** Welcome-screen inputs (see WelcomeScreen). Steps come from the whole workflow. */
interface WelcomeConfig {
  steps: WelcomePreviewStep[];
  heading?: string;
  subheading?: string;
  logoUrl?: string | null;
}

/**
 * One screen of the applicant's journey — the method they'll actually be shown
 * for one workflow step.
 */
export interface JourneyScreen {
  /** Stable identity for the screen (the workflow step id). */
  key: string;
  /**
   * Catalogue operationId of the step's first-priority enabled API.
   *
   * Empty for a step that calls no provider — see `node`.
   */
  operationId: string;
  /** This step's Return Data asks for date_of_birth / age, so PAN adds the field. */
  dobRequired?: boolean;
  /** The step's display name, captioned under the phone. */
  label?: string;
  /**
   * Ready-made screens, for a step type that has no catalogue API to look any up by. One entry per
   * applicant screen, in order.
   *
   * Questionnaire was the first of these: it collects answers rather than calling a provider, so it
   * has no `operationId`, and the lookup below is keyed on exactly that. Without this the step would
   * be filtered straight out — the flow would preview as if it were not there, which is the same
   * silent omission the DigiLocker comment above describes.
   *
   * AN ARRAY, not a single node, because Email Verification needs two (address, then code) for the
   * same reason Face Match does. It was a single `node` + `cta` pair while Questionnaire was the
   * only member; supporting two shapes for "screens this step supplies" would have meant every
   * reader handling both.
   */
  screens?: readonly PreviewScreenSpec[];
}

/** One ready-made screen supplied by a provider-less step. */
export interface PreviewScreenSpec {
  node: ReactNode;
  /** Defaults to Continue. */
  cta?: string;
}

export interface MobilePreviewProps {
  /** Controlled screen index. Omit for internal state. */
  screen?: number;
  onScreenChange?: (screen: number) => void;
  /** Selected workflow step type — used only for the loading fallback. */
  stepType?: string;
  /**
   * The applicant's journey: one screen per workflow step, in order, showing the
   * step's FIRST-PRIORITY enabled method — what the applicant actually sees. So
   * a 4-step workflow previews as welcome + 4 screens + success, and paging
   * through walks the real flow rather than one step's alternative methods.
   *
   * Omit (undefined) to fall back to the selected step's category screens — used
   * only before the catalogue resolves the enabled bindings.
   */
  journey?: JourneyScreen[];
  /** The workflow's steps, in order, for the welcome overview at screen 0. */
  welcomeSteps?: WelcomePreviewStep[];
  /** Welcome heading override (workflow Settings); falls back to the default. */
  welcomeHeading?: string;
  /** Welcome sub-heading override (workflow Settings); falls back to the default. */
  welcomeSubheading?: string;
  /** Tenant logo (Customization) shown on the welcome screen. */
  logoUrl?: string | null;
  /**
   * Branding custom properties from `toBrandCssVars`, applied to the phone so the
   * screens pick the tenant's / workflow's colours up through `var(--brand-*)`.
   * Omit for the plain default look.
   */
  brandStyle?: CSSProperties;
}

export function MobilePreview({
  screen: controlledScreen,
  onScreenChange,
  stepType,
  journey,
  welcomeSteps = [],
  welcomeHeading,
  welcomeSubheading,
  logoUrl,
  brandStyle,
}: MobilePreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [internalScreen, setInternalScreen] = useState(0);
  const [dob, setDob] = useState('');

  const screens = buildScreens(journey ?? fallbackJourney(stepType), dob, setDob, {
    steps: welcomeSteps,
    heading: welcomeHeading,
    subheading: welcomeSubheading,
    logoUrl,
  });
  const total = screens.length;
  const successIdx = total - 1;

  const rawScreen = controlledScreen ?? internalScreen;
  const screen = Math.min(Math.max(rawScreen, 0), total - 1);
  const setScreen = (next: number) => {
    const clamped = Math.min(Math.max(next, 0), total - 1);
    onScreenChange?.(clamped);
    if (controlledScreen === undefined) setInternalScreen(clamped);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (!rect) return;
      const s = Math.min((rect.width - 40) / PHONE_W, (rect.height - 40) / PHONE_H, 1);
      setScale(Math.max(s, 0.1));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Typed as PreviewScreen so the empty-workflow fallback carries the same shape
  // as a real screen (the footer reads `footerNote` unconditionally).
  const current: PreviewScreen = screens[screen] ??
    screens.at(-1) ?? { node: null, cta: 'Done', key: 'empty' };

  // The welcome overview is the one screen the designer pads more tightly (24px
  // instead of 40px), because its step list is a full-width stack of cards.
  const isOverview = current.key === 'welcome';

  return (
    <div
      ref={containerRef}
      className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-[#F4F6FD] ring-1 ring-[#E5E7EB]"
    >
      <div
        className="relative shrink-0"
        style={{ width: PHONE_W * scale * 0.85, height: PHONE_H * scale * 0.85 }}
      >
        <div
          className="absolute left-0 top-0 flex flex-col items-center justify-center"
          style={{
            width: PHONE_W,
            height: 850,
            transform: `scale(${scale * 0.85})`,
            transformOrigin: 'top left',
          }}
        >
          {/* Phone — the designer's own frame image, at their exact geometry
              (388×802, screen inset 70px top / 24px elsewhere). The bezel, side
              buttons, dynamic island, clock and status icons are all drawn into
              the PNG, which is why the top inset is so deep and why there's no
              hand-drawn status bar here. */}
          <div
            // `brand-scope` (which sets the branded font family) is applied only
            // when branding is actually passed, so an unstyled preview keeps the
            // app's own typography instead of silently switching fonts.
            className={cn(
              'relative flex shrink-0 flex-col select-none drop-shadow-xl',
              brandStyle && 'brand-scope',
            )}
            style={{
              ...brandStyle,
              width: PHONE_W,
              height: PHONE_H,
              paddingTop: 70,
              paddingBottom: 24,
              // The designer's `.sc-workflow-preview__phone` is 40px on the sides,
              // dropping to 24px only for the welcome overview
              // (`.sc-workflow-preview__phone--overview`). We had 24px on every
              // screen, which is why our step screens ran edge-to-edge while theirs
              // had visible breathing room down both sides.
              paddingLeft: isOverview ? 24 : 40,
              paddingRight: isOverview ? 24 : 40,
              backgroundImage: `url(${mobileFrame})`,
              backgroundSize: '100% 100%',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
            }}
          >
            {/* Screen — ONE rounded, clipping container around both the scrolling
                content and the fixed footer. It has to be a single container: with
                the radius on the content alone, the footer's white background kept
                its square corners and spilled over the frame's rounded bottom. */}
            <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-[32px] bg-white">
              {/* Content */}
              <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto hide-scrollbar">
                {current.node}
              </div>

              {/* Footer — CTA, then any per-screen note, then the fixed
                  "Secured by SignCare" line, which shows on EVERY screen. */}
              <div className="mt-auto flex flex-col gap-2.5 bg-white px-1 pt-4">
                <button
                  type="button"
                  // Continue walks the journey one screen at a time — welcome, then
                  // each step's method screen in order, ending on success — so the
                  // preview plays back the flow the applicant actually gets.
                  onClick={() => setScreen(Math.min(screen + 1, successIdx))}
                  className="w-full border py-3 text-[length:var(--text-16)] font-medium"
                  // Branded via the --brand-btn-* vars (Customization → Button
                  // Color / Corner Radius), falling back to the primary button.
                  style={{
                    backgroundColor: 'var(--brand-btn-fill, var(--color-primary))',
                    borderColor: 'var(--brand-btn-stroke, var(--color-primary))',
                    color: 'var(--brand-btn-text, #fff)',
                    borderRadius: 'var(--brand-btn-radius, 8px)',
                  }}
                >
                  {current.cta}
                </button>
                {current.footerNote}
                <div className="flex items-center justify-center gap-1.5 text-[length:var(--text-10)] text-primary">
                  <span>Secured by</span>
                  <img
                    src={logoColor}
                    alt="SignCare"
                    className="h-3 w-auto max-w-[84px] shrink-0 object-contain"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Which screen this is — captioned under the phone, as the designer's
              preview does. Only the step screens carry a label; the welcome overview
              and the success screen are self-evident, so they show no text.

              The element is rendered ALWAYS, with one line's height reserved, rather
              than being conditionally mounted. The container is `justify-center`, so
              when this block used to vanish on the welcome and success screens the
              column got ~37px shorter (mt-4 + one line) and centring slid the whole
              phone down by half of that — the jump visible when paging to the last
              screen. Reserving the line keeps the column a constant height, so the
              phone stays put.

              `min-h-[1.5em]` with `leading-[1.5]` rather than a hardcoded pixel value:
              the two are the same number by construction, so the reserved box is
              exactly one rendered line even if --text-14 is retuned later. */}
          <p className="mt-4 min-h-[1.5em] text-center text-[length:var(--text-14)] font-semibold leading-[1.5] text-[#041A3A]">
            {current.label}
          </p>

          {/* Pagination dots + arrows */}
          <div className="mb-2 mt-3 flex shrink-0 items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setScreen(screen - 1)}
              disabled={screen === 0}
              className="mx-1 flex h-7 w-7 items-center justify-center rounded-lg border border-[#F3F4F6] bg-white text-black shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeft size={18} strokeWidth={2.5} />
            </button>
            {screens.map((s, i) => (
              <button
                key={s.key}
                type="button"
                onClick={() => setScreen(i)}
                className={cn(
                  'rounded-full transition-all',
                  screen === i ? 'h-2 w-4 bg-[#111827]' : 'h-2 w-2 bg-[#D1D5DB]',
                )}
              />
            ))}
            <button
              type="button"
              onClick={() => setScreen(screen + 1)}
              disabled={screen === total - 1}
              className="mx-1 flex h-7 w-7 items-center justify-center rounded-lg border border-[#F3F4F6] bg-white text-black shadow-[0_2px_8px_rgba(0,0,0,0.08)] transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronRight size={18} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
